import { memo, useCallback, useEffect, useRef, useState } from "react";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { useFireworkPlayerEvents } from "../../hooks/useFireworkPlayerEvents";
import {
  claimSharedMediaPlayer,
  computeEffectiveIsLive,
  getUnifiedMediaCardThumbnail,
  isEventForPlayer,
  playMutedPreview,
  SHARED_PLAYER_ACTIVATE_EVENT,
  stopPreview,
  timeSinceEnded as utilTimeSinceEnded,
  triggerPlayerFullscreen,
  isSoftPipOverlayHiddenForEncodedId,
} from "../../utils/mediaUtils";
import type { UnifiedMediaCardProps } from "../../types/livelanding.types";
import { canUseHoverPreview, hasHoverPreviewSource } from "./unifiedMediaCard/CardHoverPreview";
import { MediaVariantCard } from "./unifiedMediaCard/MediaVariantCard";
import { WebVariantCard } from "./unifiedMediaCard/WebVariantCard";
import {
  useMediaIntersectionObserver,
  usePlayerLifecycle,
} from "./unifiedMediaCard/useUnifiedMediaCardLifecycle";

const videoDescriptionMap: Record<string, string> = {};

function activateSharedPlayer(
  encodedId: string | undefined,
  setIsPlayerActive: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (!encodedId) return;

  claimSharedMediaPlayer(encodedId);
  setIsPlayerActive(true);
  triggerPlayerFullscreen(`player-${encodedId}`);
}

function handleThumbnailKeyDown(
  event: React.KeyboardEvent<HTMLButtonElement>,
  encodedId: string | undefined,
  setIsPlayerActive: React.Dispatch<React.SetStateAction<boolean>>,
  onThumbnailClick: UnifiedMediaCardProps["onThumbnailClick"]
) {
  if (!encodedId) return;

  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    activateSharedPlayer(encodedId, setIsPlayerActive);
    onThumbnailClick?.(encodedId);
  }
}

function UnifiedMediaCardComponent({
  video,
  onMouseEnter,
  onMouseLeave,
  onThumbnailClick,
  variant,
  sectionId,
  isLive,
  timeSinceEndedWeb,
  ended_at,
  size,
  liveOverride,
  textColor = "dark",
}: Readonly<UnifiedMediaCardProps>) {
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const isCardHoveredRef = useRef(false);
  const [isPlayerActive, setIsPlayerActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [pipThumbnailOverlayHidden, setPipThumbnailOverlayHidden] = useState(false);
  const encodedId = video?.trailer?.encoded_id;
  const videoDescription = video?.event_description || video?.trailer?.description || "NA";
  const canHoverPreview = hasHoverPreviewSource(video);
  // Preview loader is visual-only; never gate player open on GIF/trailer ready.
  const isPreviewLoading =
    isCardHovered &&
    canHoverPreview &&
    !isPreviewReady &&
    !pipThumbnailOverlayHidden &&
    !isPlayerActive &&
    !isLoading;

  useEffect(() => {
    const syncOverlayFromStorage = () => {
      setPipThumbnailOverlayHidden(isSoftPipOverlayHiddenForEncodedId(encodedId));
    };

    syncOverlayFromStorage();
    if (typeof window === "undefined") return;

    const onStorage = () => {
      syncOverlayFromStorage();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", syncOverlayFromStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", syncOverlayFromStorage);
    };
  }, [encodedId]);

  useEffect(() => {
    const handleFwProductClick = (event: Event) => {
      const detail = (event as CustomEvent).detail;

      const clickedVideoId = detail?.video?.encoded_id || detail?.name?.replace("player-", "");

      //  only track THIS card's video
      if (clickedVideoId !== encodedId) return;

      const videoId = clickedVideoId || "";

      //  1. capture FW description (same as StoryBlock)
      const fwDescription = detail?.video?.description;

      //  2. store it in map (important)
      if (videoId && fwDescription) {
        videoDescriptionMap[videoId] = fwDescription;
      }

      //  3. use exact same fallback chain as StoryBlock
      const productName =
        fwDescription ||
        videoDescriptionMap[videoId] ||
        video?.trailer?.description ||
        video?.event_description ||
        "NA";
      const productTitle = detail?.product?.title || detail?.product?.product_name || "View Offers";
      const productId = detail?.product_id || detail?.product?.id || "NA";

      const cleverTapObject = {
        EVENT_NAME: "LIVE_COMMERCE_PAGE_CLICKED",
        EP_COMPONENT_NAME: "explore_top_products",
        EP_CLICK_TYPE: "video_card_button_click",
        EP_REFERENCE_ID: "explore_top_products",
        EP_PROPERTY_TYPE: "clp",

        EP_SECTION: "Explore Top Products",
        EP_CTA: "PRODUCT_CLICK",
        EP_CTA_TEXT: "PRODUCT_CLICK",

        EP_PAGE_NAME: "live-page",
        EP_EVENT_TYPE: "click",

        EP_JOURNEY: "LIVE_LANDING",
        EP_JOURNEY_NAME: "LIVE_LANDING",

        EP_PRODUCT_NAME: productName,
        EP_PRODUCT_TITLE: productTitle,
        EP_PRODUCT_ID: productId,

        EP_PRODUCT_CATEGORY: `${productName} || ${encodedId}`,

        EP_DESTINATION: detail?.product?.custom_cta_url || detail?.product?.url || "NA",
      };

      dataLayer("", {}, cleverTapObject);
    };

    const events = ["fw:shopping:product-clicked"];
    events.forEach((e) => document.addEventListener(e, handleFwProductClick));

    return () => {
      events.forEach((e) => document.removeEventListener(e, handleFwProductClick));
    };
  }, [encodedId, video?.event_description, video?.trailer?.description]);

  const closeLoader = useCallback(() => setIsLoading(false), []);

  const shouldHandlePlayerEvent = useCallback(
    (event: Event) => {
      const detail = (event as CustomEvent).detail;
      return isEventForPlayer(detail, encodedId);
    },
    [encodedId]
  );

  const handlePlayerTransition = useCallback(
    (event: Event) => {
      if (!shouldHandlePlayerEvent(event)) return;
      const detail = (event as CustomEvent).detail;
      const transitionTo = detail?.transitionTo ?? detail?.to ?? detail?.state;

      const normalizedTransition =
        typeof transitionTo === "string" ? transitionTo.trim().toLowerCase() : "";
      if (normalizedTransition === "minimized" || normalizedTransition === "minimized state") {
        setPipThumbnailOverlayHidden(true);
      }

      if (normalizedTransition === "default" || normalizedTransition === "fullscreen") {
        setPipThumbnailOverlayHidden(false);
      }

      if (!transitionTo || transitionTo !== "opening") {
        closeLoader();
      }
    },
    [closeLoader, shouldHandlePlayerEvent]
  );

  const handlePlayerQuit = useCallback(
    (event: Event) => {
      if (!shouldHandlePlayerEvent(event)) return;
      setPipThumbnailOverlayHidden(false);
      setIsPlayerActive(false);
      closeLoader();
    },
    [closeLoader, shouldHandlePlayerEvent]
  );

  useFireworkPlayerEvents({
    enabled: true,
    onTransition: handlePlayerTransition,
    onQuit: handlePlayerQuit,
  });

  useEffect(() => {
    if (!encodedId) return;

    const handleSharedActivate = (event: Event) => {
      const detail = (event as CustomEvent<{ encodedId?: string }>).detail;
      const nextEncodedId = detail?.encodedId;
      if (!nextEncodedId || nextEncodedId === encodedId) return;

      setIsPlayerActive(false);
      setIsLoading(false);
    };

    document.addEventListener(SHARED_PLAYER_ACTIVATE_EVENT, handleSharedActivate);
    return () => {
      document.removeEventListener(SHARED_PLAYER_ACTIVATE_EVENT, handleSharedActivate);
    };
  }, [encodedId]);

  const thumbnail = getUnifiedMediaCardThumbnail({ video, variant });

  const effectiveIsLive = computeEffectiveIsLive({
    isLiveProp: isLive,
    liveOverride,
    startedAt: video?.started_at ?? null,
    endedAt: video?.ended_at ?? null,
  });

  const endedAtTimeString = utilTimeSinceEnded(ended_at ?? video?.ended_at ?? null);

  const viewsCount = Number(video?.trailer?.views_count ?? 0);

  const handlePreviewReady = useCallback(() => {
    if (!isCardHoveredRef.current) return;
    setIsPreviewReady(true);
    playMutedPreview(previewVideoRef.current);
  }, []);

  const handleMouseEnter = () => {
    // Touch devices synthesize mouseenter without mouseleave; skip hover previews there.
    if (!canUseHoverPreview()) return;
    isCardHoveredRef.current = true;
    setIsPreviewReady(false);
    setIsCardHovered(true);
    if (encodedId) onMouseEnter?.(encodedId);
  };
  const handleMouseLeave = () => {
    isCardHoveredRef.current = false;
    setIsCardHovered(false);
    setIsPreviewReady(false);
    stopPreview(previewVideoRef.current);
    if (encodedId) onMouseLeave?.(encodedId);
  };
  const clearHoverPreview = () => {
    isCardHoveredRef.current = false;
    setIsCardHovered(false);
    setIsPreviewReady(false);
    stopPreview(previewVideoRef.current);
  };

  const handleClick = () => {
    if (isPlayerActive) {
      if (encodedId) {
        setIsLoading(true);
        claimSharedMediaPlayer(encodedId);
        triggerPlayerFullscreen(`player-${encodedId}`);
      }
      return;
    }
    // Open player immediately even if hover GIF/trailer is still loading.
    clearHoverPreview();
    if (encodedId) {
      setIsLoading(true);
      activateSharedPlayer(encodedId, setIsPlayerActive);

      onThumbnailClick?.(encodedId, {
        productName: videoDescription,
        productCategory: `${videoDescription} || ${encodedId}`,
      });
    }
  };
  usePlayerLifecycle(encodedId);
  useMediaIntersectionObserver(variant, previewVideoRef);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isPlayerActive) {
      if ((event.key === "Enter" || event.key === " ") && encodedId) {
        event.preventDefault();
        setIsLoading(true);
        claimSharedMediaPlayer(encodedId);
        triggerPlayerFullscreen(`player-${encodedId}`);
      }
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      clearHoverPreview();
    }
    handleThumbnailKeyDown(event, encodedId, setIsPlayerActive, (id) => {
      onThumbnailClick?.(id, {
        productName: videoDescription,
        productCategory: `${videoDescription} || ${id}`,
      });
    });
  };

  if (variant === "web") {
    return (
      <WebVariantCard
        sectionId={sectionId}
        encodedId={encodedId}
        thumbnail={thumbnail}
        video={video}
        viewsCount={viewsCount}
        effectiveIsLive={effectiveIsLive}
        timeSinceEndedWeb={timeSinceEndedWeb}
        isPlayerActive={isPlayerActive}
        isLoading={isLoading}
        isCardHovered={isCardHovered}
        isPreviewReady={isPreviewReady}
        isPreviewLoading={isPreviewLoading}
        pipThumbnailOverlayHidden={pipThumbnailOverlayHidden}
        videoRef={previewVideoRef}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        handleClick={handleClick}
        handleKeyDown={handleKeyDown}
        onPreviewReady={handlePreviewReady}
      />
    );
  }

  return (
    <MediaVariantCard
      encodedId={encodedId}
      size={size}
      textColor={textColor}
      thumbnail={thumbnail}
      video={video}
      ended_at={ended_at}
      effectiveIsLive={effectiveIsLive}
      endedAtTimeString={endedAtTimeString}
      viewsCount={viewsCount}
      isPlayerActive={isPlayerActive}
      isLoading={isLoading}
      isCardHovered={isCardHovered}
      isPreviewReady={isPreviewReady}
      isPreviewLoading={isPreviewLoading}
      pipThumbnailOverlayHidden={pipThumbnailOverlayHidden}
      previewVideoRef={previewVideoRef}
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
      handleClick={handleClick}
      handleKeyDown={handleKeyDown}
      onPreviewReady={handlePreviewReady}
    />
  );
}

function getCardVideoKey(video: UnifiedMediaCardProps["video"]): string {
  const trailer = video?.trailer;
  return [
    video?.id,
    video?.encoded_id,
    trailer?.encoded_id,
    video?.status,
    video?.started_at,
    video?.ended_at,
    video?.event_name,
    video?.event_description,
    trailer?.views_count,
    trailer?.file_url,
    trailer?.thumbnail_url,
    trailer?.captions_url,
  ]
    .map((value) => String(value ?? ""))
    .join("|");
}

function areUnifiedMediaCardPropsEqual(
  prev: Readonly<UnifiedMediaCardProps>,
  next: Readonly<UnifiedMediaCardProps>
): boolean {
  return (
    getCardVideoKey(prev.video) === getCardVideoKey(next.video) &&
    prev.variant === next.variant &&
    prev.sectionId === next.sectionId &&
    prev.isLive === next.isLive &&
    prev.timeSinceEndedWeb === next.timeSinceEndedWeb &&
    prev.isAllTab === next.isAllTab &&
    prev.activeCategory === next.activeCategory &&
    prev.language === next.language &&
    prev.ended_at === next.ended_at &&
    prev.size === next.size &&
    prev.liveOverride === next.liveOverride &&
    prev.textColor === next.textColor
  );
}

export const UnifiedMediaCard = memo(UnifiedMediaCardComponent, areUnifiedMediaCardPropsEqual);
